import { Box, InputAdornment, Popover, Stack, Typography } from '@mui/material';
import { Button, Dropdown, BhiFormDialog, TextInput } from 'src/UIKit/components';
import React, { Fragment, useEffect, useState } from 'react';
import { ColorPicker, IColor, useColor } from 'react-color-palette';
import 'react-color-palette/css';
import { useSelector } from 'react-redux';
import {
  useCreateAppointmentTypeApi,
  useUpdateAppointmentTypeApi,
} from 'src/modules/calendar/api';
import { useBlocklistApi } from 'src/modules/blocklist/api/BlocklistApi';
import { useGetUsersApi } from 'src/api/users';
import { GetUsersParams } from 'src/api/users/types';
import BannedWordsModal from 'src/components/BannedWordsModal/view';
import { AppointmentType, User } from 'src/constants/types';
import {
  addAppointmentTypeValidationSchema,
  ValidationErrors,
  hexRegex,
} from 'src/constants/validations';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import * as Yup from 'yup';
import { getRandomHexColor } from 'src/modules/settings/helpers';


type CreateAppointmentTypeModalType = {
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  initialAppointmentType: AppointmentType;
  appointmentType: AppointmentType;
  refreshAppointmentTypes: () => void;
};

export default function CreateAppointmentTypeModal({
  show,
  setShowModal,
  initialAppointmentType,
  appointmentType,
  refreshAppointmentTypes,
}: CreateAppointmentTypeModalType) {
  const itemsPerPage = 10000;

  const [errors, setErrors] = useState<ValidationErrors>({});
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const createAppointmentType = useCreateAppointmentTypeApi();
  const updateAppointmentType = useUpdateAppointmentTypeApi();
  const validateText = useBlocklistApi().validate;
  const getUsers = useGetUsersApi();
  const { profile } = useSelector(selectProfile);
  const [formValues, setFormValues] = useState<AppointmentType>(appointmentType);
  const [color, setColor] = useColor(formValues.color);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [companyUsers, setCompanyUsers] = useState<User[]>();
  const [assignedToId, setAssignedToId] = useState<string>(appointmentType.assignedTo?.id || "");

  const [delayPeriod, setDelayPeriod] = useState<string>("m");
  const [delayValue, setDelayValue] = useState<string>();

  const [showbannedWordsModal, setShowBannedWordsModal] = useState<boolean>(false);

  const isValidHexColor = (hex: string): boolean => {
    return hexRegex.test(hex);
  };

  const updateColor = (color: IColor) => {
    setColor(color);
    setFormValues({ ...formValues, color: color.hex });
  };

  const updateColorComplete = (color: IColor) => {
    setColor(color);
    setFormValues({ ...formValues, color: color.hex });
  };

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }
    setFormValues(appointmentType);
    setAssignedToId(appointmentType.assignedTo?.id || "");

    const reqQuery: GetUsersParams = {
      companyId: selectedCompanyId,
      limit: itemsPerPage,
      offset: 0,
    };
    getUsers(reqQuery)
      .then(({ data }) => {
        setCompanyUsers(data.items);
      })
      .catch((e) => {
        console.log(e);
      });
    if (appointmentType.notification) {
      setDelayValue(appointmentType.notification ? appointmentType.notification.slice(0, -1) : "");
      setDelayPeriod(
        appointmentType.notification ? appointmentType.notification[appointmentType.notification.length - 1] : "m"
      );
    }
  }, [appointmentType]);

  const handleClose = () => {
    setFormValues({ ...initialAppointmentType, color: getRandomHexColor() });
    setShowModal(false);
    setErrors({});
    setDelayPeriod("m");
    setDelayValue("");
    setAssignedToId("");
    setShowColorPicker(false);
    setColorAnchor(null);
  };

  const formatDelayNotification = (notification?: string): string | undefined => {
    return notification ? (notification.startsWith("-") ? notification : "-" + notification) : undefined;
  };

  const validateBlockListAndSave = (skipValidation: boolean = false) => {
    if (skipValidation) {
      handleAppointmentTypeSave();
    } else {
      validateText({
        texts: [formValues.name || "valid", formValues.location || "valid", formValues.description || "valid"],
      })
        .then(() => {
          handleAppointmentTypeSave();
        })
        .catch((error: { code: number }) => {
          if (error.code === 422) {
            setShowBannedWordsModal(true);
          }
          console.log(error);
        });
    }
  };

  const handleAppointmentTypeSave = () => {
    const isValid = validate(formValues);
    if (isValid && selectedCompanyId) {
      if (formValues.id) {
        updateAppointmentType(
          formValues.name,
          formValues.color,
          formValues.id,
          selectedCompanyId,
          formValues.location || undefined,
          formValues.description || undefined,
          formatDelayNotification(formValues.notification),
          assignedToId || undefined
        )
          .then(() => {
            refreshAppointmentTypes();
            handleClose();
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        createAppointmentType(
          formValues.name,
          formValues.color,
          selectedCompanyId,
          formValues.location || undefined,
          formValues.description || undefined,
          formatDelayNotification(formValues.notification),
          assignedToId || undefined
        )
          .then((response) => {
            setFormValues(response.data);
            handleClose();
            refreshAppointmentTypes();
          })
          .catch((error) => {
            if (error.error === "conflict") {
              setErrors({
                name: "Calendar with this name already exists",
              });
            }
            if (error.message && error.message.includes("duplicate key")) {
              setErrors({
                name: "Calendar with this name already exists",
              });
            }
            console.log(error);
          });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormValues({ ...formValues, color: value });
    if (isValidHexColor(value)) {
      try {
        setColor({ hex: value } as IColor);
      } catch (error) {
        console.warn('Invalid color format:', value);
      }
    }
  };

  const isValidForm = (values: typeof formValues) => {
    try {
      addAppointmentTypeValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      return true;
    } catch {
      return false;
    }
  };

  const handleDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDelayValue(value);
    setFormValues({
      ...formValues,
      notification: value ? `${value}${delayPeriod}` : undefined,
    });
  };

  const handleNotificationPeriodChange = (period: string) => {
    setDelayPeriod(period);
    setFormValues({
      ...formValues,
      notification: `${delayValue}${period}`,
    });
  };

  const handleAssignedToChange = (value: string) => {
    setAssignedToId(value);
  };

  const validate = (values: typeof formValues) => {
    try {
      addAppointmentTypeValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const formatAssignedToName = (contact: User): string => {
    if (contact.firstName || contact.lastName) {
      return `${contact.firstName || ""} ${contact.lastName || ""}`;
    }

    return contact.phoneNumber;
  };

  return (
    <Fragment>
      <BhiFormDialog
        onClose={handleClose}
        open={show}
        title={
          <>
            {profile?.type === "user" ? "" : formValues.id ? "Update " : "Create "}
            {"Calendar"}
          </>
        }
        actions={profile?.role?.name === "company_user" ? (
          <Button variant="contained" onClick={handleClose}>Close</Button>
        ) : (
          <Button
            disabled={!isValidForm(formValues)}
            variant="contained"
            onClick={() => validateBlockListAndSave()}
          >
            {formValues.id ? "Save" : "Create"}
          </Button>
        )}
      >
        <TextInput
          autoComplete="off"
          label="Calendar"
          name="name"
          disabled={profile?.role?.name === "company_user"}
          value={formValues.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          margin="none"
        />
        <TextInput
          autoComplete="off"
          label="Color"
          name="color"
          onFocus={event => {
            setColorAnchor(event.currentTarget);
            setShowColorPicker(true);
          }}
          disabled={profile?.role?.name === "company_user"}
          value={formValues.color}
          onChange={handleColorChange}
          error={!!errors.color}
          helperText={errors.color}
          margin="none"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {formValues.color && isValidHexColor(formValues.color) && (
                  <Box
                    bgcolor={formValues.color}
                    color={formValues.color.toLowerCase() === "#ffffff" ? "black" : "white"}
                    borderRadius={1}
                    px={1}
                  >
                    {formValues.color.toUpperCase()}
                  </Box>
                )}
              </InputAdornment>
            ),
          }}
        />
        <Popover
          open={showColorPicker}
          anchorEl={colorAnchor}
          onClose={() => setShowColorPicker(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          disableAutoFocus
          disableEnforceFocus
          PaperProps={{ onMouseLeave: () => setShowColorPicker(false) }}
        >
          <ColorPicker
            onChangeComplete={updateColorComplete}
            height={80}
            hideInput={["hsv", "rgb"]}
            color={color}
            onChange={updateColor}
          />
        </Popover>
        <Dropdown
          label="Assignee"
          value={assignedToId}
          options={[
            { value: "", title: "Unassigned", entity: null },
            ...(companyUsers || []).map(user => ({
              value: user.id,
              title: formatAssignedToName(user),
              entity: user,
            })),
          ]}
          onChange={(_, value) => handleAssignedToChange(value)}
          margin="none"
        />
        <Box>
          <Typography variant="subtitle2">Calendar Attachment Defaults</Typography>
          <Typography variant="body2" color="text.secondary">
            The following fields are optional. They are used as default values for new calendar events and the optional
            calendar invites sent to contacts as part of Calendar Event Reminder campaigns.
          </Typography>
        </Box>
        <TextInput
          autoComplete="off"
          label="Default Location"
          name="location"
          value={formValues.location}
          onChange={handleChange}
          error={!!errors.location}
          helperText={errors.location}
          margin="none"
        />
        <Stack direction="row" gap={2}>
          <TextInput
            label="Calendar Notification"
            name="delay"
            type="number"
            inputProps={{ min: 1 }}
            value={delayValue?.replace("-", "")}
            onChange={handleDelayChange}
            margin="none"
          />
          <Dropdown
            label="Period"
            value={delayPeriod}
            options={[
              { value: "m", title: "Minutes", entity: "m" },
              { value: "h", title: "Hours", entity: "h" },
              { value: "d", title: "Days", entity: "d" },
            ]}
            onChange={(_, value) => handleNotificationPeriodChange(value)}
            margin="none"
          />
        </Stack>
        <TextInput
          autoComplete="off"
          label="Default Description"
          multiline
          rows={3}
          name="description"
          value={formValues.description}
          onChange={handleChange}
          error={!!errors.description}
          helperText={errors.description}
          margin="none"
        />
      </BhiFormDialog>
      <BannedWordsModal
        isOpen={showbannedWordsModal}
        onConfirm={() => {
          validateBlockListAndSave(true);
          setShowBannedWordsModal(false);
        }}
        onCancel={() => setShowBannedWordsModal(false)}
      />
    </Fragment>
  );
}
